import * as React from 'react';

import { enumUnit, Unit } from '@rematter/frontend-lib-api-client';
import {
  calculateNetWeight,
  convertWeight,
  TouchNumberField,
} from '@rematter/ui-web';
import { useAtomValue, useSetAtom } from 'jotai';

import { ItemFieldProps } from '../_types';

import {
  $lastFocusedRowId,
  $lastFocusedWeightFieldType,
} from '~/modules/trade/_state/materialsInterface.state';
import { useTakeWeightListener } from '~/modules/trade/hooks/useScaleReading';
import { useScaleTakeWeight } from '~/modules/trade/hooks/useScaleWeight';
import { useWeightConversion } from '~/modules/trade/hooks/useWeightConversion';
import { useTranslations } from '~/modules/trade/i18n/useTranslations';
import { enumLoadWeightType } from '~/modules/trade/types/load';
import { toWeight } from '~/modules/trade/utils/weight';
import { useUIPreferences } from '~/shared/hooks';

//
// ⚛️ ⚛️ ⚛️ TARE WEIGHT FIELD ⚛️ ⚛️ ⚛️
//
export const TareWeightField: React.FC<ItemFieldProps> = ({
  lineItem,
  disabled,
  onUpdateItem,
  weightUnit = 'price',
  facilityDefaultUnit = enumUnit.Pounds,
}) => {
  // Translations
  const t = useTranslations();

  // Data hooks
  const { touchModeEnabled } = useUIPreferences();
  const { convertToDisplayUnit, convertFromDisplayUnit, massUnitAbbreviation } =
    useWeightConversion({
      weightUnit,
      facilityDefaultUnit,
      pricingUnit: (lineItem.pricingUnit as Unit) ?? enumUnit.Pounds,
    });

  // Memoized values
  const tareWeight = lineItem.weights?.[enumLoadWeightType.Tare];
  const grossWeight = lineItem.weights?.[enumLoadWeightType.Gross];
  const storedUnit = React.useMemo(
    () => (tareWeight?.unit as Unit) ?? enumUnit.Pounds,
    [tareWeight?.unit],
  );

  const isQuantityBased = React.useMemo(
    () => lineItem.pricingUnit === enumUnit.Each,
    [lineItem.pricingUnit],
  );

  const isOutboundTaggedMaterial = React.useMemo(
    () => lineItem.type === 'Tagged' && !!lineItem.tagId,
    [lineItem.type, lineItem.tagId],
  );

  const tareWeightObj = React.useMemo(
    () => (tareWeight ? toWeight(tareWeight.value, storedUnit) : undefined),
    [tareWeight, storedUnit],
  );

  const displayValue = React.useMemo(() => {
    if (!tareWeightObj) return 0;
    return convertToDisplayUnit(tareWeightObj);
  }, [tareWeightObj, convertToDisplayUnit]);

  // Callbacks
  const handleTareWeightChange = React.useCallback(
    (value: number) => {
      const convertedValue = convertFromDisplayUnit(value, storedUnit);
      const newTareWeight = toWeight(convertedValue, storedUnit);

      onUpdateItem(lineItem.id, {
        weights: {
          ...lineItem.weights,
          [enumLoadWeightType.Tare]: newTareWeight,
          [enumLoadWeightType.Net]: calculateNetWeight(
            grossWeight ?? null,
            newTareWeight,
          ),
        },
      });
    },
    [
      lineItem.id,
      lineItem.weights,
      grossWeight,
      storedUnit,
      convertFromDisplayUnit,
      onUpdateItem,
    ],
  );

  // Atoms
  const lastFocusedRowId = useAtomValue($lastFocusedRowId);
  const lastFocusedWeightFieldType = useAtomValue($lastFocusedWeightFieldType);
  const setLastFocusedWeightFieldType = useSetAtom($lastFocusedWeightFieldType);

  const { takeWeight } = useScaleTakeWeight();

  const applyScaleReading = React.useCallback(
    (scaleValue: number, scaleUnit: Unit) => {
      const convertedValue = convertWeight(scaleValue, scaleUnit, storedUnit);
      const newTareWeight = toWeight(convertedValue, storedUnit);

      onUpdateItem(lineItem.id, {
        weights: {
          ...lineItem.weights,
          [enumLoadWeightType.Tare]: newTareWeight,
          [enumLoadWeightType.Net]: calculateNetWeight(
            grossWeight ?? null,
            newTareWeight,
          ),
        },
      });
    },
    [lineItem.id, lineItem.weights, grossWeight, storedUnit, onUpdateItem],
  );

  const handleKeyDown = React.useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === ' ') {
        e.preventDefault();
        const reading = takeWeight();

        if (reading?.stable) {
          applyScaleReading(
            reading.grossWeight,
            reading.unitOfMeasurement ?? enumUnit.Pounds,
          );
        }
      }
    },
    [takeWeight, applyScaleReading],
  );

  useTakeWeightListener(
    ({ weight, unitOfMeasurement }) => {
      if (
        lastFocusedRowId === lineItem.id &&
        lastFocusedWeightFieldType === 'tare'
      ) {
        applyScaleReading(weight, unitOfMeasurement);
      }
    },
    [
      lastFocusedRowId,
      lastFocusedWeightFieldType,
      lineItem.id,
      applyScaleReading,
    ],
  );

  const handleFocus = React.useCallback(() => {
    setLastFocusedWeightFieldType('tare');
  }, [setLastFocusedWeightFieldType]);

  return (
    <TouchNumberField
      aria-label={t.scaleTicketPro.columns.tareWeight}
      isDisabled={isOutboundTaggedMaterial || disabled}
      value={displayValue}
      onChange={handleTareWeightChange}
      onKeyDown={handleKeyDown}
      onFocus={handleFocus}
      formatOptions={{ minimumFractionDigits: 0, maximumFractionDigits: 2 }}
      touchModeEnabled={touchModeEnabled}
      suffix={massUnitAbbreviation}
      prefix={
        isQuantityBased
          ? undefined
          : t.scaleTicketPro.itemsInterface.tareWeightPrefix
      }
      className="@TareWeightField"
    />
  );
};
